// RBAC helpers — filter master rows by user hierarchy, build "My Tasks" lists
import { departmentForRisk } from './riskUniverse';
import { lineageForDept, resolveDeptId } from './orgTree';

/**
 * Returns true if the user is allowed to see this row, per HMSI's hierarchical model:
 *  - Department Head: only rows assigned to THEIR department
 *  - Division Head: rows assigned to ANY department under their division
 *  - Operating Head: rows assigned to ANY department under their domain
 *  - Admin / Legal: everything
 */
export function canSeeRow(user, row) {
  if (!user) return false;
  const rowDeptId = resolveDeptId(row.assignedDepartment);
  const lineage = rowDeptId ? lineageForDept(rowDeptId) : null;

  if (user.role === 'department_head') {
    return rowDeptId === user.department;
  }
  if (user.role === 'division_head') {
    return !!lineage && lineage.divId === user.division;
  }
  if (user.role === 'operating_head') {
    return !!lineage && lineage.ohId === user.operatingHead;
  }
  return true; // admin, legal — full visibility
}

export function filterRowsForUser(user, rows) {
  return (rows || []).filter((r) => canSeeRow(user, r));
}

/**
 * My Tasks for a Department Head — flatten Fraud/PCC/DLC rows assigned to the dept
 * and annotate with module, dueDate (30d from now if missing), status, linkedRiskId.
 */
export function buildMyTasks({ user, masters, formResponses }) {
  if (!user || user.role !== 'department_head') return [];
  const tasks = [];
  const modules = [
    { key: 'fraud', label: 'Fraud', idField: 'controlId', titleField: 'controlQuestion' },
    { key: 'pcc', label: 'PCC', idField: 'questionId', titleField: 'complianceRequirement' },
    { key: 'dlc', label: 'DLC', idField: 'questionId', titleField: 'declarationQuestion' },
  ];
  modules.forEach((m) => {
    const rows = (masters?.[m.key] || []).filter((r) => r.active !== false);
    rows.forEach((r) => {
      if (!canSeeRow(user, r)) return;
      const id = r[m.idField];
      const resp = formResponses?.[`${m.key}:${id}`];
      const today = new Date();
      // Simulate a due date 14 days from master publish — stable hash of id for demo
      const seed = (id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
      const offset = (seed % 25) - 5; // -5..+19 days
      const due = new Date(today.getTime() + offset * 86400_000);
      const overdue = !resp && due < today;
      tasks.push({
        id,
        module: m.label,
        moduleKey: m.key,
        title: r[m.titleField],
        section: r.sectionName || r.fraudCategory || r.dlcCategory || '—',
        linkedRiskId: r.linkedRiskId || null,
        evidenceRequired: r.evidenceRequired || 'No',
        assignedDepartment: r.assignedDepartment,
        ownerRole: r.ownerRole || 'Department Head',
        dueDate: due.toISOString().slice(0, 10),
        overdue,
        status: resp?.workflowStatus || (resp ? 'Submitted' : 'Draft'),
        responseValues: resp?.values || null,
      });
    });
  });
  // Sort: overdue first, then by due date
  tasks.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    return new Date(a.dueDate) - new Date(b.dueDate);
  });
  return tasks;
}

/**
 * My Risks — risks scoped to this user's hierarchy:
 *  - DH: risks where the resolved department equals user.department
 *  - DivH: risks under any dept in their division
 *  - OH: risks under any dept in their domain
 *  - admin / legal: all risks
 */
export function buildMyRisks({ user, riskUniverse }) {
  if (!user) return [];
  if (!user.role || user.role === 'admin' || user.role === 'legal') {
    return riskUniverse || [];
  }
  return (riskUniverse || []).filter((r) => {
    const rowDeptId = resolveDeptId(departmentForRisk(r));
    const lin = rowDeptId ? lineageForDept(rowDeptId) : null;
    if (!lin) return false;
    if (user.role === 'department_head') return rowDeptId === user.department;
    if (user.role === 'division_head') return lin.divId === user.division;
    if (user.role === 'operating_head') return lin.ohId === user.operatingHead;
    return false;
  });
}

/**
 * Can this user FILL forms on Fraud / PCC / DLC pages?
 *  - Department Head: Yes (sole execution role per HMSI governance model)
 *  - Everyone else: No (Admin owns framework, OH owns risk, Div/Legal review-only)
 */
export function canFillModuleForms(user) {
  if (!user) return false;
  return user.role === 'department_head';
}

/**
 * Role-based nav visibility — which sidebar items a role can see
 */
export const ROLE_NAV_PERMISSIONS = {
  admin:          { architecture: true,  myTasks: true,  governance: true,  approvals: true,  admin: true  },
  operating_head: { architecture: true,  myTasks: false, governance: true,  approvals: true,  admin: false },
  division_head:  { architecture: true,  myTasks: false, governance: true,  approvals: true,  admin: false },
  legal:          { architecture: true,  myTasks: false, governance: true,  approvals: false, admin: false },
  department_head:{ architecture: false, myTasks: true,  governance: true,  approvals: false, admin: false },
};
